import * as React from 'react'
import { Command, CommandInput, CommandList, CommandItem } from '../command'
import { Popover, PopoverContent, PopoverTrigger } from '../popover'
import { Button } from '../button'
import { type ComboboxOptionType, type ComboboxProps } from './single'
import { Checkbox } from '../checkbox'
import { useSelections } from '@/hook/use-selections'
import { isEqual } from 'lodash-es'
import { useContentWidth } from './use-content-width'
import { ComboboxLoading, ComboboxTrigger } from './common'
import { cn } from '@/lib/utils'
import { Tabs, TabsList, TabsTrigger } from '../tabs'

type UseSelections = ReturnType<typeof useSelections<string, ComboboxOptionType>>
export const ComboboxMultiContext = React.createContext<Partial<UseSelections>>({})

export function useComboboxMultiContext() {
  return React.useContext(ComboboxMultiContext)
}

function ComboboxMultiOption({ disabled, value, children, keywords = [] }: ComboboxOptionType) {
  const { isSelected, toggle } = useComboboxMultiContext()
  const display = children
  const keys = keywords.length === 0 && typeof display === 'string' ? [display] : keywords
  return (
    <CommandItem disabled={disabled} key={value} value={value} keywords={keys} onSelect={() => toggle?.(value)}>
      <div className="flex items-center gap-2 w-full">
        <Checkbox checked={isSelected?.(value)} />
        <label className="flex-1">{display}</label>
      </div>
    </CommandItem>
  )
}

export type ComboboxMultiProps = {
  value?: (string | number)[]
  onValueChange?: (value: string[]) => void
  defaultValue?: string[]
  variant?: 'default' | 'outline'
  customDisplay?: (value: string[]) => React.ReactNode
} & Omit<ComboboxProps, 'value' | 'onValueChange' | 'defaultValue'>

export type ComboboxMultiTabProps = {
  id: string
  label: string
  children: React.ReactNode
}

function ComboboxMultiTab({ id, label, children }: ComboboxMultiTabProps) {
  const { selected = [], setSelected } = useComboboxMultiContext()
  const selectedCount = React.useMemo(() => {
    const childrenArray = React.Children.toArray(children)
    return childrenArray.filter((child) => {
      if (React.isValidElement(child)) {
        return selected.includes((child.props as ComboboxOptionType).value)
      }
      return false
    }).length
  }, [children, selected])

  // 获取当前 tab 下所有可选项的值
  const allTabValues = React.useMemo(() => {
    const values: string[] = []
    React.Children.forEach(children, (child) => {
      if (React.isValidElement(child)) {
        const props = child.props as ComboboxOptionType
        if (!props.disabled && props.value) {
          values.push(props.value)
        }
      }
    })
    return values
  }, [children])

  // 判断当前 tab 是否全选
  const isAllSelected = React.useMemo(() => {
    return allTabValues.length > 0 && allTabValues.every((value) => selected.includes(value))
  }, [allTabValues, selected])

  // 处理全选/取消全选
  const handleSelectAll = () => {
    if (isAllSelected) {
      // 取消全选：从已选中列表中移除当前 tab 的所有值
      setSelected?.(selected.filter((value) => !allTabValues.includes(value)))
    } else {
      // 全选：将当前 tab 的所有值添加到已选中列表
      const newSelected = [...new Set([...selected, ...allTabValues])]
      setSelected?.(newSelected)
    }
  }

  // 判断当前 tab 是否全选或部分选中
  const checkboxState = React.useMemo(() => {
    if (allTabValues.length === 0) return false
    const selectedCount = allTabValues.filter((value) => selected.includes(value)).length
    if (selectedCount === 0) return false
    if (selectedCount === allTabValues.length) return true
    return 'indeterminate'
  }, [allTabValues, selected])

  return (
    <TabsTrigger value={id} className="flex-1 relative" asChild>
      <div className="group flex items-center !justify-start h-10">
        <Checkbox
          checked={checkboxState}
          onClick={(e) => {
            e.stopPropagation()
            handleSelectAll()
          }}
        />
        <div className="relative ml-2 mr-1">
          {label}
          <div className="absolute -bottom-2.5 left-1/2 -translate-x-1/2 w-2 h-1 rounded bg-accent hidden group-data-[state=active]:block"></div>
        </div>
        <span
          className={cn(
            'text-xs rounded-full px-1.5 h-5 leading-5 min-w-6 text-center bg-accent text-background',
            selectedCount > 0 ? ' bg-accent text-background' : 'opacity-0',
          )}
        >
          {selectedCount}
        </span>
      </div>
    </TabsTrigger>
  )
}

function ComboboxTabMulti({
  placeholder,
  defaultValue,
  searchPlaceholder = '搜索',
  showSearch = true,
  children,
  value,
  onValueChange,
  allowClear = true,
  searchKeyword,
  onSearch,
  className,
  style,
  shouldFilter,
  customDownIcon,
  customDisplay,
  variant,
  activeLight = false,
}: ComboboxMultiProps) {
  const [open, setOpen] = React.useState(false)
  const [composing, setComposing] = React.useState(false)
  const [search, setSearch] = React.useState<string | undefined>(searchKeyword)
  const [activeTab, setActiveTab] = React.useState<string>('')

  const val = (value ?? defaultValue ?? []) as string[]
  const context = useSelections(
    val.map((item) => {
      return {
        value: item,
      }
    }),
    val,
  )
  const { selected: selectedValue, setSelected } = context

  const selected = typeof selectedValue === 'string' ? [] : selectedValue
  const $trigger = React.useRef<HTMLButtonElement>(null)
  const contentWidth = useContentWidth($trigger as React.RefObject<HTMLElement>)
  const $list = React.useRef<HTMLDivElement>(null)

  // 设置初始激活的 tab
  React.useEffect(() => {
    const firstTab = React.Children.toArray(children).find((child) => {
      return React.isValidElement(child) && child.type === ComboboxMultiTab
    })
    if (React.isValidElement(firstTab)) {
      setActiveTab((firstTab as React.ReactElement<ComboboxMultiTabProps>).props.id)
    }
  }, [children])

  React.useLayoutEffect(() => {
    setTimeout(() => {
      if (!$list.current) return
      $list.current.scrollTop = 0
    }, 0)
  }, [children])

  return (
    <ComboboxMultiContext.Provider value={context}>
      <Popover
        open={open}
        onOpenChange={(open) => {
          setOpen(open)
          if (open) {
            setSelected?.(val)
          }
        }}
      >
        <PopoverTrigger asChild>
          <ComboboxTrigger
            role="combobox"
            ref={$trigger}
            allowClear={allowClear}
            selected={val.length > 0}
            activeLight={activeLight}
            onClear={() => onValueChange?.([])}
            className={className}
            aria-expanded={open}
            style={style}
            customDownIcon={customDownIcon}
            variant={variant}
          >
            {customDisplay ? (
              customDisplay(val)
            ) : val.length > 0 ? (
              <div className="flex gap-1">
                {placeholder}
                <div className="px-2 flex items-center text-center bg-accent text-background rounded-xl">{val.length}</div>
              </div>
            ) : (
              placeholder
            )}
          </ComboboxTrigger>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-full p-0" style={{ minWidth: contentWidth }}>
          <Command
            shouldFilter={shouldFilter}
            filter={(value, search, keywords = []): number => {
              // 如果没有关键词，则返回 false
              if (!keywords.length) return 0
              // 转换为小写进行比较
              search = search.toLowerCase()
              // 检查是否有任何关键词包含搜索文本
              return keywords.some((keyword) => keyword.toLowerCase().includes(search)) ? 1 : 0
            }}
          >
            <div className={cn('flex items-center gap-2 w-full p-2 pb-0', { 'mb-1': showSearch })}>
              {showSearch && (
                <CommandInput
                  value={search}
                  onValueChange={(v) => {
                    setSearch(v)
                    if ($list.current) {
                      $list.current.scrollTop = 0
                    }
                    if (composing) return
                    onSearch?.(v)
                  }}
                  onCompositionStart={() => {
                    setComposing(true)
                  }}
                  onCompositionEnd={(ev) => {
                    setComposing(false)
                    onSearch?.((ev.target as HTMLInputElement).value)
                  }}
                  placeholder={searchPlaceholder}
                  className="h-9 w-full"
                />
              )}
            </div>
            <div className="flex">
              <div>
                <div className="border-b border-active mb-2">
                  <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                    <TabsList className="w-full h-10">
                      {React.Children.map(children, (child) => {
                        if (React.isValidElement(child) && child.type === ComboboxMultiTab) {
                          return child
                        }
                        return null
                      })}
                    </TabsList>
                  </Tabs>
                </div>
                <CommandList ref={$list} className="min-w-52 max-w-120">
                  <React.Suspense fallback={<ComboboxLoading />}>
                    {React.Children.map(children, (child) => {
                      if (
                        React.isValidElement<ComboboxMultiTabProps>(child) &&
                        child.type === ComboboxMultiTab &&
                        child.props.id === activeTab
                      ) {
                        return child.props.children
                      }
                      return null
                    })}
                  </React.Suspense>
                </CommandList>
              </div>
            </div>
            <div className="py-3 px-4 flex items-center justify-end gap-2 border-t border-active">
              <Button
                className="w-20 h-8"
                onClick={() => {
                  setSelected([])
                }}
                variant="secondary"
              >
                清空
              </Button>
              <Button
                disabled={allowClear === false && selected?.length === 0}
                onClick={() => {
                  if (!isEqual(val, selected)) {
                    onValueChange?.(selected)
                  }
                  setOpen(false)
                }}
                className="w-20 h-8"
              >
                确定
              </Button>
            </div>
          </Command>
        </PopoverContent>
      </Popover>
    </ComboboxMultiContext.Provider>
  )
}

ComboboxTabMulti.Option = ComboboxMultiOption
ComboboxTabMulti.Tab = ComboboxMultiTab
export default ComboboxTabMulti
